import React from 'react';

import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowProcedureFlow from './components/WorkflowProcedureFlow';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  { label: 'Steeringルール', title: 'Steeringルールの書き方を学ぶ', active: true },
  { label: 'rfp作成', title: 'rfpを作成する', path: '/devdocs/handson/rfp' },
  {
    label: 'proposal/business',
    title: 'proposal/businessを生成する',
    path: '/devdocs/handson/proposal-business',
  },
  { label: 'usecase', title: 'usecaseを生成する', path: '/devdocs/handson/usecase' },
  { label: 'product', title: 'productの機能一覧を生成する', path: '/devdocs/handson/product' },
];

const HandsonSteeringRules: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="要件定義編 — Steeringルールの書き方"
      seriesName="要件定義編"
      tags={['Hands-on', 'Steering', 'Rules', 'product.md']}
      seriesSteps={seriesSteps}
      prevLink={{
        label: 'ストーリー導入 + プロジェクト雛形作成',
        path: '/devdocs/handson/story-scaffold',
      }}
      nextLink={{
        label: 'rfp作成',
        path: '/devdocs/handson/rfp',
      }}
    >
      <WorkflowGoalBox
        prerequisites={['「導入編 — ストーリー導入 + プロジェクト雛形作成」が完了している']}
        completionCriteria={[
          'Steeringファイルの役割と配置先を説明できる',
          '3つの標準ファイル（product / structure / tech）の違いを説明できる',
          '読み込みモード（always / fileMatch / manual）の使い分けを理解している',
          'テンプレート構造と「書くべき情報の種類」を把握している',
          '.kiro/steering/product.md を作成し、概要・ビジネスルール・ユーザーストーリー・非機能要件が記載されている（機能一覧は空でOK）',
          'セクション11のチェックリスト全項目に自信を持ってチェックを付けられる',
        ]}
      >
        Steeringルール（AIへのルールファイル）の役割・構造・書き方を理解し、product.mdの初版を作成する。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="導入編 — ストーリー導入 + プロジェクト雛形作成"
        description="前の記事"
        path="/devdocs/handson/story-scaffold"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'Steeringファイルとは何か' },
          { id: 'section-2', label: '3つの標準ファイル — まずはこれを作る' },
          { id: 'section-3', label: '読み込みモード — いつAIがルールを読むか' },
          { id: 'section-4', label: 'テンプレート — Steeringファイルの構造' },
          { id: 'section-5', label: '書くべき情報の種類' },
          { id: 'section-6', label: '段階的に育てる — ルールは一気に書かない' },
          { id: 'section-7', label: 'product.mdを書いてみる — 最初のSteeringファイル' },
          { id: 'section-8', label: '現在のプロジェクト構成' },
          { id: 'section-9', label: 'ベストプラクティス' },
          { id: 'section-10', label: 'アンチパターン' },
          { id: 'section-11', label: '次のステップに向けて' },
        ]}
      />

      {/* セクション1 */}
      <WorkflowSection title="Steeringファイルとは何か" id="section-1" number={1}>
        <WorkflowSection title="記事1を振り返る" subNumber="1.1">
          <WorkflowText>
            前回、Kiroにdocker-compose.ymlを生成してもらいました。あのとき、プロンプトに「MySQL 8.0
            / ポート3306 / DB名team_todo /
            ...」と要件を書きました。具体的に指示するほどAIの出力精度が上がることを体験したはずです。
          </WorkflowText>
          <WorkflowText>
            でも、毎回チャットで同じことを書くのは面倒です。しかも、プロジェクトには「いつも守ってほしいルール」がたくさんあります。
          </WorkflowText>
          <WorkflowList
            items={[
              'テーブル名はスネークケースで',
              'APIのエンドポイントは /v1/ で始める',
              'Javaのインデントはスペース4つ',
              'ビジネスルールの判定ロジックはこう書く',
            ]}
          />
          <WorkflowText>
            こういった「毎回伝えるのは面倒だけど、毎回守ってほしいこと」を1箇所にまとめておくファイルが
            Steeringファイル です。
          </WorkflowText>
          <WorkflowText>
            Steeringファイルは .kiro/steering/
            に配置するMarkdownファイルで、AIが作業する際のルール・制約・手順を定義します。ルールファイルでAIの振る舞いを制御する仕組みです。
          </WorkflowText>
          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Steeringファイルは「AIに渡すプロジェクトのルールブック」です。あなたがAIに「うちのプロジェクトではこういうルールで開発するから、これに従ってね」と渡す資料です。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="Steeringがないとどうなるか" subNumber="1.2">
          <WorkflowTable
            headers={['Steeringなし', 'Steeringあり']}
            rows={[
              ['毎回「テーブル名はスネークケースで」と書く', '書かなくてもAIが自動的に従う'],
              [
                'メンバーごとに指示の粒度がバラバラ → 出力もバラバラ',
                '誰が生成しても同じルールが適用される',
              ],
              [
                'コンパクションで過去の指示が消える → AIがルールを忘れる',
                'ファイルとして永続するのでコンパクションの影響を受けない',
              ],
              [
                '新しいセッションを開始するとゼロからやり直し',
                'セッションが変わってもルールは常に読み込まれる',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="配置場所" subNumber="1.3">
          <WorkflowText>
            Steeringファイルはプロジェクトルートの .kiro/steering/
            ディレクトリに配置します。Markdownファイル（.md）で書きます。特別な記法は不要です。
          </WorkflowText>
          <WorkflowDirectoryTree
            root="team-todo/"
            entries={[
              {
                name: '.kiro',
                color: '#1565c0',
                children: [
                  {
                    name: 'steering',
                    desc: 'ここにルールファイルを置く',
                    children: [
                      { name: 'product.md' },
                      { name: 'tech.md' },
                      { name: 'structure.md' },
                    ],
                  },
                ],
              },
              { name: 'src', children: [] },
              { name: 'docker-compose.yml' },
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2 */}
      <WorkflowSection title="3つの標準ファイル — まずはこれを作る" id="section-2" number={2}>
        <WorkflowText>
          Steeringファイルは何個でも作れますが、どのプロジェクトでもまず作るべき3ファイルがあります。
        </WorkflowText>
        <WorkflowTable
          headers={['ファイル', '役割', '一言で言うと']}
          rows={[
            ['product.md', 'プロダクト要件定義', '「何を作るのか」'],
            ['structure.md', 'プロジェクト構造', '「どこに何を置くのか」'],
            ['tech.md', '技術スタック・コーディング規約', '「どう書くのか」'],
          ]}
        />

        <WorkflowSection title="product.md — 「何を作るのか」" subNumber="2.1">
          <WorkflowText>
            AIが生成するコードや設計書は、最終的に「プロダクトの要件を満たす」ために存在します。product.md
            は、そのプロダクトの全体像をAIに伝えます。
          </WorkflowText>
          <WorkflowCodeBlock
            title="product.md 雛形"
            language="markdown"
            collapsible
            code={`---
inclusion: always
---

# プロダクト要件定義

## 概要
- {このシステムが解決する課題の要約}

## 機能一覧
| 機能 | 概要 |
|------|------|
| {機能名} | {何ができるか} |

## ビジネスルール
- {ドメイン固有の判定ロジック・制約}

## ユーザーストーリー
- {誰}として、{何をしたい}。なぜなら{理由}。

## 非機能要件
| 項目 | 要件 |
|------|------|
| {性能/セキュリティ等} | {具体的な数値や方針} |`}
          />
          <WorkflowPracticeBox
            type="best"
            title="書くべき情報"
            items={[
              {
                practice: 'アプリケーションの概要',
                effect: 'AIが「何のためのシステムか」を理解した上で判断できる',
              },
              {
                practice: '機能一覧',
                effect: '全体のスコープを把握し、関連機能を考慮した設計ができる',
              },
              {
                practice: 'ビジネスルール',
                effect: 'ドメイン固有の判定ロジックを正しくコードに反映できる',
              },
              {
                practice: 'ユーザーストーリー',
                effect: '「誰が何をしたいか」からAPIやUIの設計方針を導ける',
              },
              {
                practice: '非機能要件',
                effect: '性能・セキュリティ等の制約を考慮した設計ができる',
              },
            ]}
          />
          <WorkflowPracticeBox
            type="anti"
            title="書かない方が良い情報"
            items={[
              {
                practice: '実装の詳細（クラス名、メソッド名）',
                effect: 'product.mdは「何を作るか」。「どう作るか」はtech.mdの責務',
              },
              {
                practice: '仮決めの未確定情報',
                effect: 'AIが確定事項として扱い、誤った前提で設計を進めてしまう',
              },
              { practice: '他システムの仕様詳細', effect: 'コンテキスト枠を圧迫する' },
              {
                practice: '長すぎる概要（10行以上）',
                effect: '後続セクションと内容が重複し矛盾が生まれる。3〜5行に収める',
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="structure.md — 「どこに何を置くのか」" subNumber="2.2">
          <WorkflowText>
            AIがクラスを作るとき「このクラスはどのパッケージに置く？」「命名規則は？」を判断する根拠です。structure.md
            がないと、AIは一般的なフレームワークの慣習で勝手に判断します。プロジェクト固有のルールがある場合は必ず書いておきます。
          </WorkflowText>
          <WorkflowCodeBlock
            title="structure.md 雛形"
            language="markdown"
            collapsible
            code={`---
inclusion: always
---

# プロジェクト構造

## ディレクトリ構成
{プロジェクトルートからのツリー図}

## パッケージ構成
ベースパッケージ: {ベースパッケージ名}

| パッケージ | 役割 | 説明 |
|---|---|---|
| {パッケージ名} | {役割} | {詳細} |

## クラス命名規則
| 種別 | 命名規則 | 例 |
|---|---|---|
| {Controller等} | {命名パターン} | {具体例} |

## レイヤー間の依存方向
{依存の方向を図示または箇条書きで}`}
          />
          <WorkflowPracticeBox
            type="best"
            title="書くべき情報"
            items={[
              { practice: 'ディレクトリ構成', effect: 'AIがファイルを正しい場所に配置できる' },
              {
                practice: 'パッケージ構成と各パッケージの責務',
                effect: 'どのロジックをどの層に書くか判断できる',
              },
              { practice: 'クラスの命名規則', effect: 'プロジェクト内で命名が統一される' },
              { practice: 'レイヤー間の依存方向', effect: '禁止される依存を防止できる' },
            ]}
          />
          <WorkflowPracticeBox
            type="anti"
            title="書かない方が良い情報"
            items={[
              {
                practice: '全クラスの一覧',
                effect: 'クラスが増えるたびにメンテナンスが必要になる',
              },
              {
                practice: '具体的なビジネスロジック',
                effect: 'structure.mdは「配置」の責務。ロジックはproduct.mdに',
              },
              { practice: 'IDEの設定', effect: '.editorconfigや.idea/で管理する' },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="tech.md — 「どう書くのか」" subNumber="2.3">
          <WorkflowText>
            AIがコードを生成するときの具体的なコーディングルールです。インデント、命名規則、使用するライブラリ、禁止事項などを定義します。
          </WorkflowText>
          <WorkflowCodeBlock
            title="tech.md 雛形"
            language="markdown"
            collapsible
            code={`---
inclusion: always
---

# 技術スタック・コーディング規約

## 技術スタック
| カテゴリ | 技術 | バージョン |
|---|---|---|
| 言語 | {言語名} | {バージョン} |
| フレームワーク | {FW名} | {バージョン} |
| DB | {DB名} | {バージョン} |
| ORM | {ORM名} | {バージョン} |
| ビルドツール | {ツール名} | {バージョン} |

## アーキテクチャ
- {採用するアーキテクチャパターンとその概要}

## コーディング規約

### フォーマット
- インデント: {スペースN個 or タブ}
- 最大行長: {N文字}
- 文字コード: {UTF-8等}

### 命名規則
| 対象 | ルール | 例 |
|------|--------|-----|
| {クラス/メソッド/変数等} | {命名パターン} | {具体例} |

### 禁止事項
- {やってはいけないこと}`}
          />
          <WorkflowPracticeBox
            type="best"
            title="書くべき情報"
            items={[
              {
                practice: '技術スタック一覧（言語・FW・バージョン）',
                effect: 'AIが正しいバージョンのAPIを使える',
              },
              { practice: 'アーキテクチャ方針', effect: '設計方針をコードに反映できる' },
              { practice: 'コーディング規約', effect: 'プロジェクト内でスタイルが統一される' },
              { practice: '禁止事項', effect: '使ってはいけないパターンを防止できる' },
            ]}
          />
          <WorkflowPracticeBox
            type="anti"
            title="書かない方が良い情報"
            items={[
              {
                practice: '言語の基本文法',
                effect: 'AIは既に知っている。コンテキスト枠の無駄遣い',
              },
              { practice: 'フレームワークのチュートリアル的説明', effect: '「ルール」だけを書く' },
              { practice: '全てのユースケースへの対応例', effect: 'ルールを書けば個別例は不要' },
            ]}
          />
        </WorkflowSection>

        <WorkflowTipBox title="3ファイルの使い分け — 迷ったときの判断基準">
          <WorkflowTable
            headers={['質問', 'Yes →']}
            rows={[
              ['この情報は「ユーザーが何をしたいか」に関するものか？', '→ product.md'],
              ['この情報は「ファイルやクラスをどこに置くか」に関するものか？', '→ structure.md'],
              ['この情報は「コードをどう書くか」に関するものか？', '→ tech.md'],
            ]}
          />
        </WorkflowTipBox>

        <WorkflowTipBox title="このハンズオンでの3ファイルの登場タイミング">
          <WorkflowTable
            headers={['ファイル', '作成タイミング', '理由']}
            rows={[
              [
                'product.md',
                'この記事（概要等）+ 要件定義編 — product作成（機能一覧追記）',
                'ストーリーから読み取れる情報は今書ける',
              ],
              [
                'structure.md',
                '実装編 — structure/techルールの作成',
                'コードを書く段階にならないと決まらない',
              ],
              ['tech.md', '実装編 — structure/techルールの作成', '同上'],
            ]}
          />
          <WorkflowLinkCard
            title="要件定義編 — product作成"
            description="product.mdの機能一覧を生成する記事"
            path="/devdocs/handson/product"
          />
          <WorkflowText>
            product.md
            は「段階的に育てる」の実践です。今はストーリーから読み取れる範囲で書き、後続ステップで情報が増えるたびに追記していきます。
          </WorkflowText>
        </WorkflowTipBox>
      </WorkflowSection>

      {/* セクション3 */}
      <WorkflowSection title="読み込みモード — いつAIがルールを読むか" id="section-3" number={3}>
        <WorkflowTable
          headers={['モード', 'いつ読み込まれるか', '設定方法']}
          rows={[
            ['always', '全てのやりとりで常に', 'デフォルト（何も書かなければこれ）'],
            ['fileMatch', '特定パターンのファイルを操作するときだけ', 'Front Matterで指定'],
            ['manual', 'ユーザーが #ファイル名 で明示的に渡したときだけ', 'Front Matterで指定'],
          ]}
        />

        <WorkflowSection title="always — 常に読み込まれる" subNumber="3.1">
          <WorkflowCodeBlock
            language="markdown"
            code={`---
inclusion: always
---

# 技術スタック

（本文）`}
          />
          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>: Front
              Matterを省略してもデフォルトでalwaysになります。fileMatchやmanualにすべきファイルでFront
              Matterを書き忘れると、関係ないルールが毎回コンテキスト枠を消費し続けます。全てのSteeringファイルにFront
              Matterを書くことを習慣にしてください。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="fileMatch — 特定ファイル操作時だけ" subNumber="3.2">
          <WorkflowCodeBlock
            language="markdown"
            code={`---
inclusion: fileMatch
globs:
  - "docs/schema/**/*.md"
---

# スキーマ定義ルール

（このルールは docs/schema/ 配下のファイルを操作するときだけ読み込まれる）`}
          />
        </WorkflowSection>

        <WorkflowSection title="manual — 明示的に渡したときだけ" subNumber="3.3">
          <WorkflowCodeBlock
            language="markdown"
            code={`---
inclusion: manual
---

# API開発ワークフロー

（チャットで #api_project_rule.md と書いたときだけ読み込まれる）`}
          />
        </WorkflowSection>

        <WorkflowSection title="なぜモードを分けるのか — コンテキスト枠の節約" subNumber="3.4">
          <WorkflowInfoBox type="important">
            <Typography variant="body2" fontWeight={600}>
              重要
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              AIが1回の応答で参照できる情報量には上限があります（コンテキスト枠）。全てのルールを
              always
              にすると、ルールだけで枠の大部分を消費してしまい、肝心の作業に使える余地が減ります。
            </Typography>
            <WorkflowTable
              headers={['方針', '結果']}
              rows={[
                ['全部always', 'ルールで枠を圧迫 → AIが作業中のファイルを十分に参照できない'],
                ['適切に分離', '必要なときに必要なルールだけ読み込む → 枠を有効活用'],
              ]}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              目安: always は標準3ファイル + 本当に常時必要な最小限に留める。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowTipBox title="チームTODO管理アプリでの使い分け例">
          <WorkflowTable
            headers={['ファイル', 'モード', '理由']}
            rows={[
              ['product.md', 'always', 'どんな作業でも「何を作っているか」は常に必要'],
              ['structure.md', 'always', 'クラスの配置先は常に参照する'],
              ['tech.md', 'always', 'コーディングルールは常に参照する'],
              ['create_proposal.md', 'fileMatch', 'proposal.mdを生成するときだけ必要'],
              ['create_business.md', 'fileMatch', 'business.mdを生成するときだけ必要'],
              ['create_usecases.md', 'fileMatch', 'usecases.mdを生成するときだけ必要'],
              ['api_dev_workflow.md', 'manual', '設計→実装→テスト横断ワークフロー。手動で読み込む'],
            ]}
          />
        </WorkflowTipBox>
      </WorkflowSection>

      {/* セクション4 */}
      <WorkflowSection title="テンプレート — Steeringファイルの構造" id="section-4" number={4}>
        <WorkflowCodeBlock
          title="基本構造"
          language="markdown"
          collapsible
          code={`---
inclusion: fileMatch
globs:
  - "対象ファイルパターン"
---

# タイトル（このルールの名前）

## このルールの目的
- このルールが何のためにあるか、1〜2行で説明

## 成果物の説明
- この成果物は何か、どういう位置づけか

## 配置先
| 項目 | 内容 |
|------|------|
| 配置先 | {ディレクトリパス} |
| ファイル名 | {命名パターン} |

## 生成フロー
- どういう手順で成果物を作るか

## 対話で確認すべき事項
- AIが人間に確認すべき項目のリスト

## テンプレート構造
- AIが生成する成果物のフォーマット定義

## 記載ルール
- 各セクションで守るべき細かいルール

## 完成後のチェックリスト
- 生成後に確認すべき品質チェック項目

## 禁止事項
- やってはいけないこと`}
        />

        <WorkflowTable
          headers={['セクション', '役割', '書かないとどうなるか']}
          rows={[
            [
              'Front Matter',
              '読み込みモードを指定',
              'デフォルトでalwaysになり意図せずコンテキストを消費する',
            ],
            ['このルールの目的', 'AIに目的を伝える', 'AIがルールの適用場面を誤判断することがある'],
            [
              '成果物の説明',
              '位置づけと使われ方を伝える',
              'AIがスコープを誤解し過不足のある出力をする',
            ],
            ['配置先', '出力ファイルの保存場所', 'AIが毎回異なる場所・名前で出力する'],
            ['生成フロー', '手順と書き出しタイミング', 'AIが中途半端な状態で書き出す'],
            ['テンプレート構造', '成果物の構造を定義', 'AIが毎回違う構造で出力し品質がばらつく'],
            ['記載ルール', '細かい制約を明示', 'AIが細部をズレさせる'],
            ['禁止事項', 'AIがやりがちな失敗を防止', '禁止していないことはAIが良かれと思ってやる'],
          ]}
        />

        <WorkflowInfoBox type="important">
          <Typography variant="body2" fontWeight={600}>
            重要
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            「禁止事項」は特に重要です。AIは「良かれと思って」余計なことをします。例えば「RFPに書いてないカラムを親切心で追加する」「テーブルにcreated_atを勝手に足す」など。明示的に禁止しないと本当にやります。毎回やります。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowInfoBox type="info">
          <Typography variant="body2" fontWeight={600}>
            ポイント
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            基本構造の全セクションが常に必要なわけではありません。成果物の性質に応じて不要なセクションは省略してOKです。基本構造はあくまで「考慮すべき観点のチェックリスト」であり、固定フォーマットではありません。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowSection title="具体例 — 成果物生成ルールの場合" subNumber="4.1">
          <WorkflowText>
            proposal.md（技術仕様の決定事項）を生成するためのSteeringを例にします。ここでは構造を理解するのが目的なので、中身の詳細は気にしないでください。
          </WorkflowText>
          <WorkflowCodeBlock
            title="proposal生成ルール（例）"
            language="markdown"
            collapsible
            code={`---
inclusion: fileMatch
globs:
  - ".kiro/requirements/**/*.proposal.md"
  - ".kiro/requirements/**/*.rfp.md"
---

# proposal.md 生成ルール

## 概要
- RFPの決定事項を元に、技術仕様（テーブル定義・API設計）を構造化して出力する

## 出力フォーマット

### 決定事項
- 概要: {機能の全体像}
- 認証・認可: {権限要件}
- ビジネスルール: {判定ロジック}

### テーブル定義
| 論理名 | 物理名 | 種別 | Not Null | PK | 備考 |
|--------|--------|------|----------|----|----- |

### API一覧
| API ID | API名 | Method | Endpoint | 認可 |
|--------|-------|--------|----------|------|

## 記載ルール
- テーブル定義のカラム・型・桁数はRFPの決定事項と一致させる
- API IDの採番はRFPに従う（独自に採番しない）
- マスタテーブルには初期データを含める

## 禁止事項
- RFPに記載されていないテーブル・カラム・APIを勝手に追加しない
- Not Null・桁数をRFPと食い違わせない`}
          />
          <WorkflowText>この例から読み取れるポイント:</WorkflowText>
          <WorkflowList
            variant="ordered"
            items={[
              'fileMatch で *.proposal.md と *.rfp.md に反応する → proposal生成時だけ読み込まれる',
              '出力フォーマット でテーブルの列構成まで指定 → AIが毎回同じ構造で出力する',
              '禁止事項 で「勝手に追加しない」を明示 → AIの親切心による汚染を防止',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5 */}
      <WorkflowSection title="書くべき情報の種類" id="section-5" number={5}>
        <WorkflowTable
          headers={['種類', '何を書くか', '効果']}
          rows={[
            ['フォーマット定義', '成果物の構造・テンプレート', 'AIの出力構造が毎回揃う'],
            ['記載ルール', '各セクションで守るべき制約', '細部のズレを防止する'],
            ['禁止事項', 'AIがやりがちだがやってほしくないこと', '余計な「親切」を防止する'],
            ['判断基準', '曖昧な場面でどう判断すべきか', 'AIの判断がブレなくなる'],
          ]}
        />

        <WorkflowSection title="フォーマット定義" subNumber="5.1">
          <WorkflowText>
            AIに「どんな構造で出力すればいいか」を教えます。テーブルの列定義、セクションの順序まで具体的に書きます。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="記載ルール" subNumber="5.2">
          <WorkflowText>
            フォーマットの各セクションに対する細かい制約です。「何を書くか」だけでなく「どこから情報を取るか」まで指定すると精度が上がります。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="禁止事項" subNumber="5.3">
          <WorkflowText>
            AIが「良かれと思って」やりがちなことを明示的に止めます。禁止しない限り、AIは自分の判断で追加・変更することがあります。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="判断基準" subNumber="5.4">
          <WorkflowText>
            曖昧な状況でAIがどう判断すべきかを明文化します。「適宜」「必要に応じて」と書くとAIの判断がバラつくので、具体的な基準を与えます。
          </WorkflowText>
          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              「適宜」「必要に応じて」「状況を見て」のような曖昧な表現はSteeringでは避けてください。具体的な条件分岐（if〜then〜）で書くのがコツです。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6 */}
      <WorkflowSection title="段階的に育てる — ルールは一気に書かない" id="section-6" number={6}>
        <WorkflowInfoBox type="important">
          <Typography variant="body2" fontWeight={600}>
            重要
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Steeringファイルを最初から完璧に書こうとしないでください。最初は荒くてOK。実際にAIに生成させてみて、品質が足りなかった部分をルールに追加する。このサイクルを回して育てるのが正しいアプローチです。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowTable
          headers={['理由', '説明']}
          rows={[
            [
              'AIがどこで失敗するか事前に分からない',
              '実際に生成させて初めて「ここがズレる」と分かる',
            ],
            [
              '過剰なルールは逆効果',
              'AIはルール間の優先度を判断できない。多すぎると矛盾するルールが出やすい',
            ],
            [
              'プロジェクトの仕様は段階的に固まる',
              '要件定義 → 基本設計 → 詳細設計と進むにつれて情報が増えていく',
            ],
          ]}
        />

        <WorkflowProcedureFlow
          steps={[
            { label: '① まず最小限のルールだけ書く' },
            { label: '② AIに成果物を生成させる' },
            {
              label: '③ 生成結果を確認する',
              sub: 'OK → 次の成果物へ進む / NG → 何がダメだったか特定する',
              variant: 'check',
            },
            { label: '④ ルールを追加・修正する', sub: '② に戻って再生成' },
          ]}
        />

        <WorkflowSection title="具体例 — 段階的にルールが増える過程" subNumber="6.1">
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            1回目: 最小限で試す
          </Typography>
          <WorkflowCodeBlock
            language="markdown"
            code={`## 出力フォーマット
（テンプレートだけ書く）

## 記載ルール
- RFPの決定事項に従う`}
          />
          <WorkflowText>→ 生成してみる → 「桁数がRFPと違う...」</WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            2回目: 気づいた問題をルールに追加
          </Typography>
          <WorkflowCodeBlock
            language="markdown"
            code={`## 記載ルール
- RFPの決定事項に従う
- カラムの型・桁数はRFPの決定事項と完全一致させる  ← 追加

## 禁止事項
- 桁数をRFPと食い違わせない  ← 追加`}
          />
          <WorkflowText>→ 再生成 → 「桁数は合った。でも勝手にカラムが増えてる...」</WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            3回目: さらに追加
          </Typography>
          <WorkflowCodeBlock
            language="markdown"
            code={`## 禁止事項
- 桁数をRFPと食い違わせない
- RFPに記載されていないカラムを勝手に追加しない  ← 追加`}
          />
          <WorkflowText>→ 再生成 → OK!</WorkflowText>
        </WorkflowSection>

        <WorkflowInfoBox type="info">
          <Typography variant="body2" fontWeight={600}>
            ポイント
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            このサイクルは面倒に見えますが、一度追加したルールは二度と同じ失敗をしなくなります。回を重ねるほどルールが充実し、生成品質が安定していきます。これがAI駆動開発における「投資」です。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowTipBox title="ルールが甘いとどうなるか — 後続ステップへの影響">
          <WorkflowText>
            このハンズオンでは rfp.md → proposal.md → business.md → usecases.md → product.md
            の流れで成果物を積み上げていきます。各ステップの出力は次のステップの入力になります。
          </WorkflowText>
          <WorkflowList
            items={[
              'rfpのルールが甘い → proposalの品質が下がる',
              'proposalのルールが甘い → businessの入力項目がズレる',
              'businessのルールが甘い → usecasesの処理フローが不正確になる',
            ]}
          />
          <WorkflowText>
            早い段階でルールを整えるほど、後続の手戻りが減ります。このハンズオンでは意図的に「ルールが足りなくて品質が下がる」体験をしてもらいます。
          </WorkflowText>
        </WorkflowTipBox>
      </WorkflowSection>

      {/* セクション7 */}
      <WorkflowSection
        title="product.mdを書いてみる — 最初のSteeringファイル"
        id="section-7"
        number={7}
      >
        <WorkflowText>
          ここまで知識を学んできました。ここからは実際に手を動かします。記事1の導入ストーリーを思い出してください。Aの相談内容から、product.mdの概要・ビジネスルール・ユーザーストーリー・非機能要件は書けるはずです。
        </WorkflowText>

        <WorkflowSection title="ファイルを作成する" subNumber="7.1">
          <WorkflowCodeBlock language="bash" code="mkdir -p .kiro/steering" />
        </WorkflowSection>

        <WorkflowSection title="書いてみる" subNumber="7.2">
          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              やること
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              導入ストーリーから読み取れる情報を product.md に書く。
            </Typography>
          </WorkflowInfoBox>
          <WorkflowTable
            headers={['セクション', 'ストーリーから読み取れる情報']}
            rows={[
              ['概要', 'スプレッドシート管理が破綻 → タスクの可視化が目的'],
              ['ビジネスルール', '期限超過の検知、完了状態の管理、担当者の明確化'],
              ['ユーザーストーリー', 'チームリーダー（A）として、誰が何をいつまでにやるか見たい'],
              ['非機能要件', '来月オープンまでに → 短期開発、シンプルでいい → 最小構成'],
              ['機能一覧', 'まだ書かない（usecase完了後に追記する）'],
            ]}
          />
          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              ここでは完成例を見ずに自分で書いてください。「正解」はありません。完璧でなくてOK。後から追記・修正できます。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="書き終わったら確認" subNumber="7.3">
          <WorkflowChecklist
            groups={[
              {
                title: 'チェックリスト',
                items: [
                  'Front Matterに inclusion: always を明示的に書いたか',
                  '概要が長くなりすぎていないか（3〜5行目安）',
                  'ビジネスルールがストーリーの困りごとに対応しているか',
                  '「書かない方が良い情報」に該当するもの（実装詳細、未確定情報）を書いていないか',
                  '機能一覧セクションは空または「後続ステップで追記」と書いてあるか',
                ],
              },
            ]}
          />

          <WorkflowTipBox title="なぜ今書くのか">
            <WorkflowText>
              次の記事（3-1）で rfp.md
              を書くとき、AIはコンテキストにあるproduct.mdを参照します。「このシステムは何のためのものか」がproduct.mdに書かれていれば、AIはそれを前提として一貫した提案をしてくれます。
            </WorkflowText>
            <WorkflowText>
              逆にproduct.mdがないと、AIは毎回「このシステムは何ですか？」からスタートし、回答ごとに前提がブレる可能性があります。これがセクション6「段階的に育てる」の実践です。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="product.md - 完成例" subNumber="7.4">
          <WorkflowLockedCodeBlock password="DaM1eR">
            {`---
inclusion: always
---

# プロダクト要件定義

## 概要
- チームのタスク管理をスプレッドシートから脱却し、Webアプリケーションで可視化するシステム
- 誰が何をいつまでにやるか、終わったかどうかをチーム全員がリアルタイムに把握できる

## ビジネスルール
- 期限を過ぎたTODOは「期限超過」として区別する
- 完了したTODOは一覧のデフォルト表示から除外する（フィルターで表示可能）
- 1つのTODOには必ず1人の担当者を割り当てる（担当者名は自由入力テキスト）

## ユーザーストーリー
- チームリーダーとして、メンバーのタスク状況を一覧で把握したい。期限超過に早く気づけるように。
- チームメンバーとして、自分の担当タスクと期限を確認したい。何から手をつけるか判断できるように。
- チームメンバーとして、タスクを完了にしたい。終わったことをチームに共有するために。
- チームリーダーとして、タスクを作成してメンバーに割り当てたい。口頭やチャットでの依頼漏れを防ぐために。

## 非機能要件
| 項目 | 要件 |
|------|------|
| 開発期間 | 短期（1〜2週間で動くものを作る） |
| 構成 | 最小構成（必要最低限の機能に絞る） |
| 認証 | なし（今回のハンズオンでは省略） |
| 同時利用 | チーム規模（5〜10人程度を想定） |

## 機能一覧
（後続ステップで追記する）`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション8 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-8" number={8}>
        <WorkflowDirectoryTree
          root="team-todo/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'steering',
                  children: [{ name: 'product.md', desc: '手動で追加', highlight: '#e65100' }],
                },
              ],
            },
            { name: 'docker-compose.yml' },
            { name: 'HELPME.md' },
            {
              name: 'src',
              children: [
                {
                  name: 'main',
                  children: [
                    {
                      name: 'java/com/example/teamtodo',
                      children: [
                        { name: 'TeamTodoApplication.java' },
                        { name: 'config', children: [{ name: 'SecurityConfig.java' }] },
                      ],
                    },
                    { name: 'resources', children: [{ name: 'application.yml' }] },
                  ],
                },
              ],
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション9 */}
      <WorkflowSection title="ベストプラクティス" id="section-9" number={9}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: 'always は標準3ファイル + 最小限に留める',
              effect: 'コンテキスト枠を圧迫しない',
            },
            {
              practice: '同じ成果物に対するルールは1ファイルにまとめる',
              effect: 'ルールの散在を防ぎAIが一貫した判断をできる',
            },
            { practice: '「禁止事項」を明示する', effect: 'AIの「良かれと思って」を防止する' },
            {
              practice: '曖昧な表現を避け具体的な条件分岐で書く',
              effect: 'AIの判断がブレなくなる',
            },
            {
              practice: '実際に生成して品質が落ちた箇所だけルールを追加する',
              effect: '過剰ルールを防ぎ本当に必要なルールだけ残る',
            },
            {
              practice: 'ルールの追加理由をコメントで残す',
              effect: '後からメンテナンスするとき理由が分かる',
            },
            {
              practice: 'fileMatchのglobsは対象成果物のパターンに合わせる',
              effect: '意図しないタイミングでの発動を防止する',
            },
            {
              practice: '定期的にルールを棚卸しする',
              effect: '使われなくなったルールがコンテキストを無駄に消費し続けるのを防ぐ',
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション10 */}
      <WorkflowSection title="アンチパターン" id="section-10" number={10}>
        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: '全てのSteeringを always にする',
              effect: 'コンテキスト枠を圧迫しAIの応答品質が全体的に下がる',
            },
            { practice: '最初から完璧なルールを書こうとする', effect: '書き過ぎて矛盾が生まれる' },
            {
              practice: '「適宜」「必要に応じて」を多用する',
              effect: 'AIの判断がバラつき生成するたびに結果が違う',
            },
            {
              practice: '1つのルールファイルが長大になる',
              effect: 'AIが重要な部分を見落とす可能性が増える',
            },
            {
              practice: '関連するルールを複数ファイルに分散させる',
              effect: '片方のルールだけ読み込まれて不整合な出力になる',
            },
            {
              practice: 'ルールを書かずに毎回プロンプトで指示する',
              effect: 'セッションが変わると指示が消える',
            },
            {
              practice: 'AIの出力が悪いのにルールを見直さない',
              effect: '同じ問題が何度も繰り返される',
            },
            {
              practice: '使わなくなったルールを放置する',
              effect: '不要なルールがコンテキストを消費し続ける',
            },
            {
              practice: '同じglobs に対して複数のSteeringファイルを作る',
              effect: 'どのファイルに何が書いてあるか人間も把握しづらくなる',
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション11 */}
      <WorkflowSection title="次のステップに向けて" id="section-11" number={11}>
        <WorkflowText>
          この記事では、Steeringルールの知識を身につけました。次の記事からは、いよいよ実際に手を動かします。
        </WorkflowText>
        <WorkflowText>
          次のステップでは、Aの相談内容をもとに
          rfp.md（要件草案）を自分で書きます。その後、proposal/business生成のためのSteeringルールを実際に書き、AIに成果物を生成させます。
        </WorkflowText>
        <WorkflowInfoBox type="preview">
          <Typography variant="body2" fontWeight={600}>
            次の記事: 要件定義編 — rfp作成
          </Typography>
          <WorkflowList
            items={[
              '導入ストーリーの困りごとを「システム要件」に落とし込む',
              'rfp.mdを自力で書く',
              '曖昧な相談 → 具体的な要件にする練習',
            ]}
          />
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* チェックリスト */}
      <WorkflowChecklist
        groups={[
          {
            title: 'チェックリスト',
            items: [
              'Steeringファイルの配置場所（.kiro/steering/）を知っている',
              '標準3ファイル（product / structure / tech）の違いを説明できる',
              '読み込みモード（always / fileMatch / manual）の使い分けを理解している',
              'Front Matterの書き方（inclusion / globs）を理解している',
              'Steeringに書くべき4種類の情報（フォーマット/記載ルール/禁止事項/判断基準）を把握している',
              '「禁止事項」が特に重要な理由を説明できる',
              'ルールは段階的に育てるものであり最初から完璧を目指さないと理解している',
              'always を増やしすぎるリスクを理解している',
              '.kiro/steering/product.md を作成した',
              'product.mdに概要・ビジネスルール・ユーザーストーリー・非機能要件が記載されている',
              'product.mdの機能一覧は空または「後続ステップで追記」としている',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonSteeringRules;
